import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { formatEuro } from "@/lib/format";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 10;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [phase, setPhase] = useState("polling"); // polling | paid | failed | expired
  const [status, setStatus] = useState(null);
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setPhase("failed");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const r = await api.get(`/billing/status?session_id=${encodeURIComponent(sessionId)}`);
        setStatus(r.data);
        if (r.data.payment_status === "paid" || r.data.payment_status === "no_payment_required") {
          setPhase("paid");
          return;
        }
        if (r.data.status === "expired") {
          setPhase("expired");
          return;
        }
      } catch {}
      if (attemptsRef.current >= MAX_ATTEMPTS) {
        setPhase("failed");
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Auto-navigate to dashboard on paid after a short victory pause
  useEffect(() => {
    if (phase === "paid") {
      const t = setTimeout(() => navigate("/dashboard", { replace: true }), 2400);
      return () => clearTimeout(t);
    }
  }, [phase, navigate]);

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center">
        {phase === "polling" && (
          <>
            <Loader2 className="mx-auto mb-4 text-[#00E575] animate-spin" size={48} />
            <h1 className="font-display text-2xl font-bold mb-2">Zahlung wird geprüft …</h1>
            <p className="text-sm text-zinc-300 font-medium">Bitte einen Moment Geduld.</p>
          </>
        )}
        {phase === "paid" && (
          <>
            <CheckCircle2 className="mx-auto mb-4 text-[#00E575]" size={56} />
            <h1 className="font-display text-2xl font-bold mb-2">Zahlung erfolgreich! 🎉</h1>
            <p className="text-sm text-zinc-400 mb-2">
              Dein Abo ist aktiv. Du wirst gleich zum Dashboard weitergeleitet.
            </p>
            {status?.amount_total != null && (
              <p className="text-[12px] text-zinc-300 font-mono">
                Beleg-Summe: {formatEuro(status.amount_total / 100)}
                {status.currency && status.currency.toUpperCase() !== "EUR" && ` (${status.currency.toUpperCase()})`}
              </p>
            )}
          </>
        )}
        {phase === "expired" && (
          <>
            <AlertCircle className="mx-auto mb-4 text-amber-400" size={48} />
            <h1 className="font-display text-2xl font-bold mb-2">Sitzung abgelaufen</h1>
            <p className="text-sm text-zinc-400 mb-4">Bitte starte den Bezahlvorgang erneut.</p>
            <button onClick={() => navigate("/checkout", { replace: true })} className="bg-[#00E575] text-[#09090B] font-bold px-5 h-11 rounded-xl">
              Neu starten
            </button>
          </>
        )}
        {phase === "failed" && (
          <>
            <AlertCircle className="mx-auto mb-4 text-red-400" size={48} />
            <h1 className="font-display text-2xl font-bold mb-2">Zahlung nicht bestätigt</h1>
            <p className="text-sm text-zinc-400 mb-4">Die Bestätigung dauerte länger als erwartet. Schau im Dashboard nach.</p>
            <button onClick={() => navigate("/dashboard", { replace: true })} className="bg-zinc-800 border border-zinc-700 hover:border-[#00E575] hover:text-[#00E575] px-5 h-11 rounded-xl text-zinc-200">
              Zum Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
